/**
 * Custom Konva filters for advanced photo adjustments
 * HSL per-channel adjustments and RGB curves
 */

import type {
  HslChannelId,
  HslChannelAdjustments,
  CurvesAdjustment,
  CurvesChannelId,
  CurvesPoint,
  LUTSettings,
} from '../types';
import { HSL_CHANNEL_HUE_RANGES, DEFAULT_HSL_CHANNELS, DEFAULT_CURVES } from './photoAdjustments';

// ==================== Color Conversion Utilities ====================

/** Convert RGB to HSL (all values 0-1) */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return [0, 0, l]; // achromatic
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return [h, s, l];
}

/** Convert HSL to RGB (all values 0-1) */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    return [l, l, l]; // achromatic
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return [
    hue2rgb(p, q, h + 1/3),
    hue2rgb(p, q, h),
    hue2rgb(p, q, h - 1/3),
  ];
}

/** Determine which HSL channel a hue belongs to (0-360 degrees input) */
function getHslChannelForHue(hueDeg: number): HslChannelId | null {
  // Normalize to 0-360
  const h = ((hueDeg % 360) + 360) % 360;

  for (const [channelId, range] of Object.entries(HSL_CHANNEL_HUE_RANGES) as [HslChannelId, typeof HSL_CHANNEL_HUE_RANGES[HslChannelId]][]) {
    if (range.min > range.max) {
      // Wraps around (e.g., red: 345-15)
      if (h >= range.min || h <= range.max) {
        return channelId;
      }
    } else {
      if (h >= range.min && h <= range.max) {
        return channelId;
      }
    }
  }

  return null;
}

/** Calculate blend factor for smooth transitions between channels */
function getChannelBlendFactor(hueDeg: number, channelId: HslChannelId): number {
  const range = HSL_CHANNEL_HUE_RANGES[channelId];
  const h = ((hueDeg % 360) + 360) % 360;

  let min = range.min;
  let max = range.max;

  // Handle wrap-around case
  if (min > max) {
    if (h >= min) {
      // Hue is in the upper part (e.g., 345-360)
      max = 360 + max;
    } else {
      // Hue is in the lower part (e.g., 0-15)
      min = min - 360;
    }
  }

  // Check if hue is in range
  if (h < min || h > max) return 0;

  const rangeWidth = max - min;
  const center = (min + max) / 2;
  const distance = Math.abs(h - center);
  const halfWidth = rangeWidth / 2;

  // Smooth falloff from center
  return 1 - (distance / halfWidth) * 0.5;
}

// ==================== Curves Utilities ====================

/** Build lookup table for a curves channel */
function buildCurvesLUT(points: CurvesPoint[]): Uint8Array {
  const lut = new Uint8Array(256);

  if (points.length < 2) {
    // Linear fallback
    for (let i = 0; i < 256; i++) {
      lut[i] = i;
    }
    return lut;
  }

  // Sort points by x
  const sorted = [...points].sort((a, b) => a.x - b.x);

  for (let i = 0; i < 256; i++) {
    const x = i / 255;

    // Find surrounding points
    let p0 = sorted[0];
    let p1 = sorted[sorted.length - 1];

    for (let j = 0; j < sorted.length - 1; j++) {
      if (x >= sorted[j].x && x <= sorted[j + 1].x) {
        p0 = sorted[j];
        p1 = sorted[j + 1];
        break;
      }
    }

    // Linear interpolation between points
    const t = p1.x === p0.x ? 0 : (x - p0.x) / (p1.x - p0.x);
    const y = p0.y + t * (p1.y - p0.y);

    lut[i] = Math.max(0, Math.min(255, Math.round(y * 255)));
  }

  return lut;
}

// ==================== Custom Konva Filters ====================

export interface HslCurvesFilterConfig {
  hslChannels?: HslChannelAdjustments;
  curves?: CurvesAdjustment;
}

/**
 * Create a Konva-compatible filter function for HSL per-channel and curves
 * Returns null if no adjustments are needed
 */
export function createHslCurvesFilter(config: HslCurvesFilterConfig): ((imageData: ImageData) => void) | null {
  const { hslChannels, curves } = config;

  // Check if any adjustments are active
  const hasHslChanges = hslChannels && Object.values(hslChannels).some(
    ch => ch.hue !== 0 || ch.saturation !== 0 || ch.luminance !== 0
  );

  const hasCurvesChanges = curves?.enabled && Object.values(curves.channels).some(
    ch => ch.points.length !== 2 ||
          ch.points[0].x !== 0 || ch.points[0].y !== 0 ||
          ch.points[1].x !== 1 || ch.points[1].y !== 1
  );

  if (!hasHslChanges && !hasCurvesChanges) {
    return null;
  }

  // Pre-build curves LUTs
  let rgbLut: Uint8Array | null = null;
  let rLut: Uint8Array | null = null;
  let gLut: Uint8Array | null = null;
  let bLut: Uint8Array | null = null;

  if (hasCurvesChanges && curves) {
    rgbLut = buildCurvesLUT(curves.channels.rgb?.points ?? [{ x: 0, y: 0 }, { x: 1, y: 1 }]);
    rLut = buildCurvesLUT(curves.channels.r?.points ?? [{ x: 0, y: 0 }, { x: 1, y: 1 }]);
    gLut = buildCurvesLUT(curves.channels.g?.points ?? [{ x: 0, y: 0 }, { x: 1, y: 1 }]);
    bLut = buildCurvesLUT(curves.channels.b?.points ?? [{ x: 0, y: 0 }, { x: 1, y: 1 }]);
  }

  return (imageData: ImageData) => {
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];

      // Apply curves first
      if (rgbLut && rLut && gLut && bLut) {
        // Apply master RGB curve
        r = rgbLut[r];
        g = rgbLut[g];
        b = rgbLut[b];

        // Apply per-channel curves
        r = rLut[r];
        g = gLut[g];
        b = bLut[b];
      }

      // Apply HSL per-channel adjustments
      if (hasHslChanges && hslChannels) {
        // Convert to HSL
        const [h, s, l] = rgbToHsl(r / 255, g / 255, b / 255);
        const hueDeg = h * 360;

        // Accumulate adjustments from overlapping channels
        let hueShift = 0;
        let satShift = 0;
        let lumShift = 0;
        let totalWeight = 0;

        for (const channelId of Object.keys(hslChannels) as HslChannelId[]) {
          const adj = hslChannels[channelId];
          if (!adj || (adj.hue === 0 && adj.saturation === 0 && adj.luminance === 0)) {
            continue;
          }

          const weight = getChannelBlendFactor(hueDeg, channelId);
          if (weight > 0) {
            hueShift += (adj.hue / 360) * weight;
            satShift += (adj.saturation / 100) * weight;
            lumShift += (adj.luminance / 100) * weight;
            totalWeight += weight;
          }
        }

        if (totalWeight > 0) {
          // Apply normalized adjustments
          let newH = h + hueShift;
          let newS = Math.max(0, Math.min(1, s + satShift));
          let newL = Math.max(0, Math.min(1, l + lumShift));

          // Normalize hue
          if (newH < 0) newH += 1;
          if (newH > 1) newH -= 1;

          // Convert back to RGB
          const [newR, newG, newB] = hslToRgb(newH, newS, newL);
          r = Math.round(newR * 255);
          g = Math.round(newG * 255);
          b = Math.round(newB * 255);
        }
      }

      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
    }
  };
}

/**
 * Konva-compatible filter that applies HSL per-channel and curves adjustments
 * This is designed to work with Konva's filter system
 */
export function applyHslCurvesFilter(
  imageData: ImageData,
  hslChannels?: HslChannelAdjustments,
  curves?: CurvesAdjustment
): void {
  const filter = createHslCurvesFilter({ hslChannels, curves });
  if (filter) {
    filter(imageData);
  }
}

// ==================== Film Grain Filter ====================

/**
 * Create a film grain filter
 * @param intensity 0-100, strength of grain effect
 */
export function createGrainFilter(intensity: number): (imageData: ImageData) => void {
  // Normalize intensity to 0-1 range
  const amount = Math.max(0, Math.min(100, intensity)) / 100;

  // Pre-generate noise pattern for performance
  const noiseCache = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    noiseCache[i] = Math.random() * 255;
  }

  return (imageData: ImageData) => {
    const data = imageData.data;
    const grainStrength = amount * 25; // Max grain adjustment ±25

    for (let i = 0; i < data.length; i += 4) {
      // Use hash function for pseudo-random noise (deterministic per pixel)
      const noiseIndex = (i / 4) % 256;
      const noise = (noiseCache[noiseIndex] - 128) * (grainStrength / 128);

      // Apply grain to RGB channels
      data[i] = Math.max(0, Math.min(255, data[i] + noise));         // R
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise)); // G
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise)); // B
      // Alpha unchanged
    }
  };
}

// ==================== Vignette Filter ====================

/**
 * Create a vignette filter
 * @param intensity 0-100, strength of vignette darkening
 */
export function createVignetteFilter(intensity: number): (imageData: ImageData) => void {
  const amount = Math.max(0, Math.min(100, intensity)) / 100;

  return (imageData: ImageData) => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDist = Math.sqrt(centerX * centerX + centerY * centerY);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;

        // Calculate distance from center (normalized 0-1)
        const dx = x - centerX;
        const dy = y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;

        // Vignette formula: darkening increases with distance
        // Using quadratic falloff for natural look
        const vignette = 1 - (dist * dist * amount);

        // Apply vignette to RGB channels
        data[i] = Math.max(0, data[i] * vignette);         // R
        data[i + 1] = Math.max(0, data[i + 1] * vignette); // G
        data[i + 2] = Math.max(0, data[i + 2] * vignette); // B
        // Alpha unchanged
      }
    }
  };
}

// ==================== LUT Color Grading Filter ====================

/**
 * Create a Konva-compatible filter that applies a 3D LUT with trilinear interpolation.
 * Returns null if no LUT is active or intensity is 0.
 */
export function createLUTFilter(lutSettings?: LUTSettings): ((imageData: ImageData) => void) | null {
  if (!lutSettings || !lutSettings.enabled || !lutSettings.lut || lutSettings.intensity <= 0) {
    return null;
  }

  const { lut, intensity } = lutSettings;
  const lutSize = lut.size;
  const lutData = lut.data;
  const scale = lutSize - 1;
  const intensityFactor = intensity / 100;

  return (imageData: ImageData) => {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;

      // Find LUT grid indices
      const rIdx = r * scale;
      const gIdx = g * scale;
      const bIdx = b * scale;

      const r0 = Math.floor(rIdx);
      const g0 = Math.floor(gIdx);
      const b0 = Math.floor(bIdx);
      const r1 = Math.min(r0 + 1, scale);
      const g1 = Math.min(g0 + 1, scale);
      const b1 = Math.min(b0 + 1, scale);

      const rFrac = rIdx - r0;
      const gFrac = gIdx - g0;
      const bFrac = bIdx - b0;

      // 8 corner indices in the 3D LUT
      const idx = (ri: number, gi: number, bi: number) =>
        (bi * lutSize * lutSize + gi * lutSize + ri) * 3;

      const c000 = idx(r0, g0, b0);
      const c001 = idx(r0, g0, b1);
      const c010 = idx(r0, g1, b0);
      const c011 = idx(r0, g1, b1);
      const c100 = idx(r1, g0, b0);
      const c101 = idx(r1, g0, b1);
      const c110 = idx(r1, g1, b0);
      const c111 = idx(r1, g1, b1);

      // Trilinear interpolation for each RGB channel
      for (let ch = 0; ch < 3; ch++) {
        const v00 = lutData[c000 + ch] * (1 - rFrac) + lutData[c100 + ch] * rFrac;
        const v01 = lutData[c001 + ch] * (1 - rFrac) + lutData[c101 + ch] * rFrac;
        const v10 = lutData[c010 + ch] * (1 - rFrac) + lutData[c110 + ch] * rFrac;
        const v11 = lutData[c011 + ch] * (1 - rFrac) + lutData[c111 + ch] * rFrac;

        const v0 = v00 * (1 - gFrac) + v10 * gFrac;
        const v1 = v01 * (1 - gFrac) + v11 * gFrac;

        const lutValue = v0 * (1 - bFrac) + v1 * bFrac;

        // Blend with original based on intensity
        const original = [r, g, b][ch];
        const blended = original * (1 - intensityFactor) + lutValue * intensityFactor;
        data[i + ch] = Math.max(0, Math.min(255, Math.round(blended * 255)));
      }
    }
  };
}
